import { useCallback, useState } from 'react';

const STORAGE_KEY = 'steward.showFills';

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // storage unavailable (private browsing, restricted context, etc.) --
    // default to consolidated rather than let a page fail to render.
    return false;
  }
}

/**
 * Shared "Show fills" preference for leg tables, backed by localStorage.
 * Default is `false` (consolidated, one row per contract at a weighted
 * average price -- Schwab's own positions view). `true` shows the raw
 * per-fill rows the rank-1 pipeline actually stores.
 *
 * try/catch wraps every storage access: losing the persisted preference is
 * fine, losing the page to a thrown `localStorage` call is not.
 */
export function useShowFills(): [boolean, (next: boolean) => void] {
  const [showFills, setShowFillsState] = useState<boolean>(readStored);

  const setShowFills = useCallback((next: boolean) => {
    setShowFillsState(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // In-memory state still updates for this session even if it can't persist.
    }
  }, []);

  return [showFills, setShowFills];
}
